import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const CompanyContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CompanyProvider({ children }) {
  const { token, isAuthenticated } = useAuth();
  const [ownCompany, setOwnCompany] = useState(null);
  const [clientCompany, setClientCompany] = useState(null);
  const [activeClientContext, setActiveClientContext] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch own company settings
  const fetchOwnCompany = useCallback(async () => {
    if (!token || !isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/company`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOwnCompany(data);
      }
    } catch (error) {
      console.error('Failed to fetch company:', error);
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated]);

  // Fetch client company when context changes
  const fetchClientCompany = useCallback(async (companyId) => {
    if (!token || !companyId) {
      setClientCompany(null);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/accountant/clients/company/${companyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClientCompany(data);
      }
    } catch (error) {
      console.error('Failed to fetch client company:', error);
      setClientCompany(null);
    }
  }, [token]);

  useEffect(() => {
    fetchOwnCompany();
  }, [fetchOwnCompany]);

  // Called by AccountantContext when client context changes
  const setActiveClient = useCallback((clientContext) => {
    setActiveClientContext(clientContext);
    if (clientContext?.company_id) {
      fetchClientCompany(clientContext.company_id);
    } else {
      setClientCompany(null);
    }
  }, [fetchClientCompany]);

  // Determine if we're in client context
  const isInClientContext = !!activeClientContext;

  // Determine active company based on context
  // Use client_details as fallback while clientCompany is loading
  const company = isInClientContext 
    ? (clientCompany || activeClientContext?.client_details || null)
    : ownCompany;

  // Get the company currency. Defaults to AED (UAE — primary market) if the
  // company has not been loaded yet or has no currency set.
  // Use client_details from activeClientContext as fallback while clientCompany is loading
  const currency = company?.currency ||
    (isInClientContext && activeClientContext?.client_details?.currency) ||
    'AED';

  // Format currency using company's currency setting.
  // - Always renders 2 decimals (so 1000 → 1,000.00)
  // - For INR uses Indian numbering system (12,34,567.89 — lakhs/crores) via en-IN locale.
  // - All other currencies use en-US international grouping (1,234,567.89 — thousand/million).
  const formatCurrency = useCallback((amount, options = {}) => {
    const {
      showSymbol = true,
      minimumFractionDigits = 2,
      maximumFractionDigits = 2
    } = options;

    const locale = currency === 'INR' ? 'en-IN' : 'en-US';

    function formatCurrencyValue(value) {
      try {
        return new Intl.NumberFormat(locale, {
          style: showSymbol ? 'currency' : 'decimal',
          currency: currency,
          minimumFractionDigits,
          maximumFractionDigits
        }).format(Number(value) || 0);
      } catch {
        // Bad currency code → fall back to plain decimal so we never crash the UI
        return (Number(value) || 0).toFixed(maximumFractionDigits);
      }
    }

    if (amount === null || amount === undefined || amount === '' || Number.isNaN(Number(amount))) {
      return showSymbol ? formatCurrencyValue(0) : '0.00';
    }

    return formatCurrencyValue(amount);
  }, [currency]);

  // Get currency symbol
  const getCurrencySymbol = useCallback(() => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    
    // Extract just the symbol
    const parts = formatter.formatToParts(0);
    const symbolPart = parts.find(part => part.type === 'currency');
    return symbolPart ? symbolPart.value : currency;
  }, [currency]);

  // Refresh company data (call after updates)
  const refreshCompany = async () => {
    await fetchOwnCompany();
    if (isInClientContext && activeClientContext?.company_id) {
      await fetchClientCompany(activeClientContext.company_id);
    }
  };

  // Expose refreshCompany globally for cross-context communication
  useEffect(() => {
    window.__refreshCompany = refreshCompany;
    return () => { delete window.__refreshCompany; };
  });

  // Get company logo URL via the redeploy-safe public endpoint that prefers
  // the DB-stored copy. Cache-buster ensures the browser refetches after upload.
  const logoUrl = company?.id && company?.logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}/api/company/${company.id}/logo?v=${encodeURIComponent(company.logo_updated_at || company.updated_at || '')}`
    : null;

  const value = {
    company,
    ownCompany,
    clientCompany,
    currency,
    logoUrl,
    loading,
    formatCurrency,
    getCurrencySymbol,
    refreshCompany,
    isInClientContext,
    activeClientContext,
    setActiveClient
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
}

// Export a hook that returns just the formatCurrency function for easy use
export function useCurrency() {
  const { formatCurrency, currency, getCurrencySymbol } = useCompany();
  return { formatCurrency, currency, getCurrencySymbol };
}
